import { Pipe, PipeTransform } from '@angular/core';

/**
 * IANA zone for US Eastern time. Named rather than a fixed offset so the conversion follows
 * daylight saving: UTC-5 in winter, UTC-4 in summer.
 */
export const EASTERN_TIME_ZONE = 'America/New_York';

/**
 * Shown alongside the time. Deliberately the generic `ET` rather than the seasonal `EST`/`EDT`,
 * so one label reads correctly year round.
 */
export const EASTERN_TIME_LABEL = 'ET';

export type EasternDateTimeFormat = 'dateTime' | 'date' | 'time';

/** `2025-09-10 14:40:33` — a naive stamp uses a space where ISO 8601 wants a `T`. */
const NAIVE_DATE_TIME = /^\d{4}-\d{2}-\d{2} \d{2}:\d{2}/;
const HAS_TIME_ZONE = /(Z|[+-]\d{2}:?\d{2})$/i;
/** `2025-09-10` — a calendar date carrying no clock time, so no zone may shift it. */
const DATE_ONLY = /^\d{4}-\d{2}-\d{2}$/;

/**
 * Reads an API timestamp as UTC.
 *
 * `DATETIMEOFFSET` columns serialize with an offset, but `DATETIME` columns come back
 * without one (`2025-09-10T18:40:33`). Both hold UTC, so a value with no designator is
 * read as UTC rather than as browser-local time.
 */
export function parseUtcDate(value: string | Date | null | undefined): Date | null {
  if (!value) {
    return null;
  }

  if (value instanceof Date) {
    return Number.isNaN(value.getTime()) ? null : value;
  }

  const trimmed = value.trim();
  if (!trimmed) {
    return null;
  }

  const isoLike = NAIVE_DATE_TIME.test(trimmed) ? trimmed.replace(' ', 'T') : trimmed;
  const date = new Date(HAS_TIME_ZONE.test(isoLike) ? isoLike : `${isoLike}Z`);

  return Number.isNaN(date.getTime()) ? null : date;
}

/**
 * Formats a UTC timestamp in US Eastern time, e.g. `09/10/2025 2:40pm ET`.
 *
 * A date-only value has no clock time to convert, so it is rendered as the calendar date
 * it already is rather than shifted a day by the zone offset.
 *
 * Returns an empty string when the value is missing or unparseable.
 */
export function formatEasternDateTime(
  value: string | Date | null | undefined,
  format: EasternDateTimeFormat = 'dateTime'
): string {
  const dateOnly = typeof value === 'string' ? DATE_ONLY.exec(value.trim()) : null;
  if (dateOnly) {
    const [year, month, day] = dateOnly[0].split('-');
    return format === 'time' ? '' : `${month}/${day}/${year}`;
  }

  const date = parseUtcDate(value);
  if (!date) {
    return '';
  }

  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone: EASTERN_TIME_ZONE,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: 'numeric',
    minute: '2-digit',
    hour12: true
  }).formatToParts(date);

  const part = (type: Intl.DateTimeFormatPartTypes): string =>
    parts.find((p) => p.type === type)?.value ?? '';

  const datePart = `${part('month')}/${part('day')}/${part('year')}`;
  const timePart = `${part('hour')}:${part('minute')}${part('dayPeriod').toLowerCase()} ${EASTERN_TIME_LABEL}`;

  switch (format) {
    case 'date':
      return datePart;
    case 'time':
      return timePart;
    default:
      return `${datePart} ${timePart}`;
  }
}

/**
 * Renders a UTC timestamp in US Eastern time so admins read upload and award times in the
 * program's own time zone rather than their browser's.
 *
 * `{{ file.uploadedDate | easternDateTime }}` -> `09/10/2025 2:40pm ET`
 * `{{ file.uploadedDate | easternDateTime: 'time' }}` -> `2:40pm ET`
 */
@Pipe({
  name: 'easternDateTime'
})
export class EasternDateTimePipe implements PipeTransform {
  transform(value: string | Date | null | undefined, format: EasternDateTimeFormat = 'dateTime'): string {
    return formatEasternDateTime(value, format);
  }
}
